import type { RichTextEditorProps } from 'mui-tiptap';
import type { TypographyProps } from '@mui/material';
import type { Editor, Extension } from '@tiptap/core';

export interface UploadedImageInfo {
  url: string;
  width?: number;
  height?: number;
  imageId?: string;
  aspectRatio?: number;
  format?: string;
}

/**
 * Props for the MuiTipTap component
 */
export interface MuiTipTapProps {
  /** Title displayed above the editor */
  title?: string;

  /** Placeholder text shown when editor is empty */
  placeholder?: string;

  /** Whether to show the toolbar */
  showToolbar?: boolean;

  /** Callback fired when editor content changes */
  onChange?: (content: string) => void;
  /** Callback for image upload - receives File and returns Promise<string> with image URL */
  onImageUpload?: (file: File) => Promise<string | UploadedImageInfo>;

  /** Callbacks to signal upload state for UI toggles */
  onImageUploadStart?: () => void;
  onImageUploadEnd?: () => void;

  /** Disable image upload button (e.g., while an upload is in progress) */
  isImageUploadDisabled?: boolean;

  /** Text displayed on the action button */
  actionButtonText?: string;

  /** Callback fired when action button is clicked */
  onAction?: () => void;

  /** Function that returns boolean to determine if action button should be disabled */
  isActionDisabled?: () => boolean;

  /** Default/initial value for the editor (HTML string) */
  defaultValue?: string;

  /** Additional Tiptap extensions to include */
  extensions?: Extension[];

  /** List of enabled toolbar features (defaults to all) */
  toolbarFeatures?: ToolbarFeature[];

  /**
   * Props to pass directly to the RichTextEditor component
   * Allows customization of editor behavior (disabled, editable, onBlur, onFocus, etc.)
   * @example { disabled: false, editable: true, onBlur: () => {} }
   */
  editorProps?: Partial<RichTextEditorProps>;

  /**
   * Props to pass directly to the StyledTitle (Typography) component
   * Allows customization of title styling (variant, color, sx, etc.)
   * @example { variant: 'h6', color: 'primary', sx: { fontWeight: 'bold' } }
   */
  titleProps?: Partial<TypographyProps>;
}

/**
 * Props for the Toolbar component
 */
export interface ToolbarProps {
  /** Tiptap editor instance */
  editor: Editor | null;

  /** Callback for image upload */
  onImageUpload: (file: File) => Promise<string | UploadedImageInfo>;

  /** Upload lifecycle callbacks */
  onUploadStart?: () => void;
  onUploadEnd?: () => void;

  /** Whether the upload button should be disabled */
  disabled?: boolean;

  /** List of enabled toolbar features */
  toolbarFeatures: ToolbarFeature[];
}

/** Supported toolbar feature keys */
export enum ToolbarFeature {
  Heading = 'heading',
  Bold = 'bold',
  Italic = 'italic',
  Underline = 'underline',
  Strikethrough = 'strikethrough',
  OrderedList = 'orderedList',
  BulletList = 'bulletList',
  Indent = 'indent',
  Outdent = 'outdent',
  ImageUpload = 'imageUpload',
}
